/**
 * Checks if a passed element has a passed class
 *
 * @param el
 * @param cls
 * @returns {boolean}
 */
export function hasClass(el, cls) {
    let result = false;

    if (el.classList) {
        result = el.classList.contains(cls);
    } else {
        result = new RegExp('(^| )' + cls + '( |$)', 'gi').test(el.cls);
    }

    return result;
}

/**
 * Adds passed class to passed element, array of elements or nodeList
 *
 * @param target
 * @param cls
 */
export function addClass(target, cls) {
    function run(el) {
        if (!hasClass(el, cls)) {
            if (el.classList) {
                el.classList.add(cls);
            } else {
                el.className += ' ' + cls;
            }
        }
    }

    if (target.constructor === NodeList || target.constructor === Array) {
        target.forEach((el) => {
            run(el);
        });
    } else {
        run(target);
    }
}

/**
 * Removed passed class from passed element, array of elements or nodeList
 *
 * @param target
 * @param cls
 */
export function removeClass(target, cls) {
    function run(el) {
        if (hasClass(el, cls)) {
            if (el.classList) {
                el.classList.remove(cls);
            } else {
                el.cls = el.cls.replace(new RegExp('(^|\\b)' + cls.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
            }
        }
    }

    if (target.constructor === NodeList || target.constructor === Array) {
        target.forEach((el) => {
            run(el);
        });
    } else {
        run(target);
    }
}

/**
 * Toggles passed class on passed element, array of elements or nodeList
 *
 * @param target
 * @param cls
 */
export function toggleClass(target, cls) {
    function run(el) {
        if (!hasClass(el, cls)) {
            addClass(el, cls);
        } else {
            removeClass(el, cls);
        }
    }

    if (target.constructor === NodeList || target.constructor === Array) {
        target.forEach((el) => {
            run(el);
        });
    } else {
        run(target);
    }
}

/**
 * Adds or removes class on passed element, array of elements or nodeList based on passed boolean
 *
 * @param target
 * @param cls
 */
export function boolClass(target, cls, bool) {
    function run(el) {
        if (!el.classList.contains(cls) && bool) {
            el.classList.add(cls);
        }

        if (el.classList.contains(cls) && !bool) {
            el.classList.remove(cls);
        }
    }

    if (target.constructor === NodeList || target.constructor === Array) {
        target.forEach((el) => {
            run(el);
        });
    } else {
        run(target);
    }
}

/**
 * Returns siblings of passed element
 *
 * @param el
 * @returns {*}
 */
export function getSiblings(el, filter) {
    const siblings = [];
    el = el.parentNode.firstChild;
    do {
        if (!filter || filter(el)) {
            siblings.push(el);
        }
    } while ((el = el.nextSibling));
    return siblings;
}

/**
 * Check on the browser to determine if the device is touch enabled
 *
 * @returns {boolean}
 */
export function isTouch() {
    let result = false;

    if (Modernizr.touchevents) {
        result = Modernizr.touchevents;
    } else {
        result = 'ontouchstart' in window || navigator.maxTouchPoints;
    }

    return result;
}

/**
 * Check on the browser to determine if the window is less than the passed width
 *
 * @param width
 * @returns {boolean}
 */
export function maxWidth(width) {
    let result = false;

    if (Modernizr.mq) {
        result = Modernizr.mq('(max-width: ' + width + 'px)');
    } else {
        const w = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        result = w < width;
    }

    return result;
}

/**
 * Check on the browser to determine if the window is greater than the passed width
 *
 * @param width
 * @returns {boolean}
 */
export function minWidth(width) {
    let result = false;

    if (Modernizr.mq) {
        result = Modernizr.mq('(min-width: ' + width + 'px)');
    } else {
        const w = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        result = w > width;
    }

    return result;
}

/**
 * Calls passed function upon document being ready
 *
 * @param fn
 */
export function documentReady(fn) {
    if (document.attachEvent ? document.readyState === 'complete' : document.readyState !== 'loading') {
        fn();
    } else {
        document.addEventListener('DOMContentLoaded', fn);
    }
}

/**
 * Determine if a passed event string is native or not
 *
 * @param name
 * @returns {boolean}
 */
export function isNativeEvent(name) {
    return typeof document.body['on' + name] !== 'undefined';
}

/**
 * Trigger a native or custom event on the passed element
 *
 * @param name
 * @param el
 */
export function trigger(name, el) {
    let event;

    if (isNativeEvent(name)) {
        event = document.createEvent('HTMLEvents');
        event.initEvent(name, true, false);
    } else {
        if (window.CustomEvent) {
            event = new CustomEvent(name, { detail: { some: 'data' } });
        } else {
            event = document.createEvent('CustomEvent');
            event.initCustomEvent(name, true, true, { some: 'data' });
        }
    }

    el.dispatchEvent(event);
}

/**
 * Calculate the position of a passed element on the window
 *
 * @param el
 * @returns {{top: number, left: number}}
 */
export function getPosition(el) {
    const box = el.getBoundingClientRect(),
        docEl = document.documentElement,
        scrollTop = window.pageYOffset,
        scrollLeft = window.pageXOffset,
        clientTop = docEl.clientTop || 0,
        clientLeft = docEl.clientLeft || 0,
        top = box.top + scrollTop - clientTop,
        left = box.left + scrollLeft - clientLeft;

    return { top: Math.round(top), left: Math.round(left) };
}

/**
 * Determine weather a given string is valid JSON
 *
 * @param str
 * @returns {boolean}
 */
export function isJsonString(str) {
    try {
        JSON.parse(str);
    } catch (e) {
        return false;
    }
    return true;
}

/**
 * Get the closest element of a given element by class
 *
 * Take an element (the first param), and traverse the DOM upward from it
 * until it hits the element with a given class name (second parameter).
 * This mimics jQuery's `.closest()`.
 *
 * @param  {element} el    The element to start from
 * @param  {string}  clazz The class name
 * @return {element}       The closest element
 */
export function closestByClass(el, clazz) {
    // Traverse the DOM up with a while loop
    while (!hasClass(el, clazz)) {
        // Increment the loop to the parent node
        el = el.parentNode;
        if (!el) {
            return null;
        }
    }
    // At this point, the while loop has stopped and `el` represents the element that has
    // the class you specified in the second parameter of the function `clazz`

    // Then return the matched element
    return el;
}

/**
 * Check to see if element or ancestors have display none
 *
 * @param  {element}  el The element to check
 * @return {Boolean}
 */
export function isElementVisible(el) {
    return !!el.offsetParent;
}

/**
 * Search aray of objects and return key where object key equals value
 *
 * @param  {array} array
 * @param  {string} key
 * @param  {mixed} value
 * @return {int}
 */
export function getArrayKeyFromChildObjectKeyValue(array, key, value) {
    for (let i = 0; i < array.length; i++) {
        if (array[i][key] === value) {
            return i;
        }
    }

    return undefined;
}

/**
 * Post form data as if usinmg window redirect but with post data
 *
 * @param  {string} path
 * @param  {object} params
 * @param  {string} method
 */
export function postForm(path, params, method) {
    method = method || 'post';

    const form = document.createElement('form');
    form.setAttribute('method', method);
    form.setAttribute('action', path);

    for (const key in params) {
        if (params.hasOwnProperty(key)) {
            const hiddenField = document.createElement('input');
            hiddenField.setAttribute('type', 'hidden');
            hiddenField.setAttribute('name', key);
            hiddenField.setAttribute('value', params[key]);

            form.appendChild(hiddenField);
        }
    }

    document.body.appendChild(form);
    form.submit();
}

/**
 * Generates HTML for WooCommerce error message and injects mesage
 *
 * @param  {string} messageContent
 */
export function displayWooErrorMessage(messageContent) {
    // If we have no notice wrapper generate it
    let noticeContainer = document.querySelector('.woocommerce-notices-wrapper');

    if (!noticeContainer) {
        document.querySelector('.woocommerce-MyAccount-content').innerHTML +=
            '<div class="woocommerce-notices-wrapper"></div>'; // eslint-disable-line max-len
        noticeContainer = document.querySelector('.woocommerce-notices-wrapper');
    }

    let errorContainer = document.querySelector('.woocommerce-notices-wrapper__notice--error .woocommerce-error');

    // if we dont have an error wrapper generate one
    if (!errorContainer) {
        let html = '<div class="woocommerce-notices-wrapper__notice woocommerce-notices-wrapper__notice--error">';
        html += '<div class="woocommerce-notices-wrapper__container">';
        html += '<ul class="woocommerce-error" role="alert">';
        html += '</ul>';
        html += '<div class="woocommerce-notices-wrapper__close">';
        html += '<span></span>';
        html += '<span></span>';
        html += '</div>';
        html += '</div>';
        html += '</div>';

        noticeContainer.innerHTML += html;

        errorContainer = document.querySelector('.woocommerce-notices-wrapper__notice--error .woocommerce-error');
    }

    // Generate and inject the message as an error
    errorContainer.innerHTML += `<li>${messageContent}</li>`;
}

/**
 * Generates HTML for WooCommerce error message and injects mesage
 *
 * @param  {string} messageContent
 */
export function displayWooSuccessMessage(messageContent) {
    // If we have no notice wrapper generate it
    let noticeContainer = document.querySelector('.woocommerce-notices-wrapper');

    if (!noticeContainer) {
        document.querySelector('.woocommerce-MyAccount-content').innerHTML +=
            '<div class="woocommerce-notices-wrapper"></div>'; // eslint-disable-line max-len
        noticeContainer = document.querySelector('.woocommerce-notices-wrapper');
    }

    // Generate and inject the message as an success
    let successMessage =
        '<div class="woocommerce-notices-wrapper__notice woocommerce-notices-wrapper__notice--success">'; // eslint-disable-line max-len
    successMessage += '<div class="woocommerce-notices-wrapper__container">';
    successMessage += '<div class="woocommerce-message" role="alert">';
    successMessage += `<p>${messageContent}</p>`;
    successMessage += '</div>';
    successMessage += '<div class="woocommerce-notices-wrapper__close">';
    successMessage += '<span></span>';
    successMessage += '<span></span>';
    successMessage += '</div>';
    successMessage += '</div>';
    successMessage += '</div>';

    noticeContainer.innerHTML += successMessage;
}

export function downloadFile(filePath) {
    const link = document.createElement('a');
    link.href = filePath;
    link.download = filePath.substr(filePath.lastIndexOf('/') + 1);
    link.click();
}
